import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { EMP, NAME, PROJECT_CODE, ROLE, TEAM } from "./consts";
import { useNavigate, useParams } from "react-router-dom";
import { schema, formValues } from "./validations";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import {
  useAddUpdateTeamMutation,
  useDeleteUserRoleMutation,
  useGetEmployeeActiveList,
  useGetRoles,
  useGetTeam,
} from "@/services";
import { toast } from "@/components";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

const useTeamAssignment = () => {
  const { action, id } = useParams();
  const isEdit = action == routes.edit;
  const isView = action == routes.view;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const projectData = useSelector(
    (state: RootState) => state.currentData.projectData
  );
  const deletedRoleIds = useRef<string[]>([]);

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [NAME]: projectData?.name || "",
      [PROJECT_CODE]: projectData?.code || "",
      [TEAM]: [{ [ROLE]: "", [EMP]: "" }],
    },
  });

  const requiredFields = getRequiredFields(schema);

  const { data: roleList, isLoading: isLoadingRoles } = useGetRoles();

  const {
    data: employeeList,
    isLoading: isEmployeeLoading,
  } = useGetEmployeeActiveList({ data: {} });

  const { data: teamList, isLoading: isLoadingTeam } = useGetTeam({
    projectId: id as string,
    queryParams: {
      enabled: !!id,
    },
  });

  useEffect(() => {
    if (teamList?.data) {
      const team = teamList?.data?.length ? teamList?.data?.filter((data: any) => data?.roleName?.toLowerCase() != "customer")?.map((data: any) => ({
        ...data,
        [ROLE]: data?.[ROLE]?.toString() || '',
        [EMP]: data?.[EMP]?.toString() || '',
      })) : [{ [ROLE]: "", [EMP]: "" }];
      form.setValue(TEAM, team);
    }
  }, [teamList]);

  const { mutateAsync: addUpdateInfo, isPending: isAddPending } =
    useAddUpdateTeamMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        // toast({ message: "Team Assign Successfully" });
      },
    });

  const { mutateAsync: deleteUserRole, isPending: isDeleteUserRolePending } =
    useDeleteUserRoleMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        // toast({ message: "Team Assign Successfully" });
      },
    });

  const onSubmit = async (data: formValues) => {
    let _data = setEmptyFieldsToNull(data);
    _data = removeEmptyFields(_data);

    try {
      const additionalPromises: Promise<any>[] = [];
      deletedRoleIds.current.map((val: string) => {
        additionalPromises.push(
          deleteUserRole({
            userRoleId: val,
          })
        );
      });

      _data[TEAM]?.map((val: any) => {
        additionalPromises.push(
          addUpdateInfo({
            projectId: id as string,
            userId: val[EMP],
            data: {
              roleId: val[ROLE],
            },
          })
        );
      });

      await Promise.all(additionalPromises);

      toast({
        message: "Roles Assigned Successfully",
        type: "success",
      });

      navigate(`/${routes.admin}/${routes.projectOverview}`)
      queryClient.invalidateQueries({queryKey: ["project-list"]})
    } catch (error) {
      console.error("Error in post-success APIs:", error);
      toast({
        message: "Something went wrong while processing additional data.",
        type: "error",
      });
    }
  };

  const onAddMoreRole = () => {
    form.setValue(TEAM, [
      ...(form.getValues()[TEAM] as any),
      { [ROLE]: "", [EMP]: "" },
    ]);
  };

  const onDeleteRole = (idx: number) => () => {
    deletedRoleIds.current.push((form.getValues()[TEAM] as any)?.[idx]?.["userRoleId"]);
    form.setValue(TEAM, [
      ...(form.getValues()[TEAM] as any)?.filter(
        (_: any, index: number) => idx != index
      ),
    ]);
  };

  return {
    form,
    onSubmit,
    isAddPending,
    requiredFields,
    isEdit,
    onAddMoreRole,
    onDeleteRole,
    isView,
    roleList,
    isLoadingRoles,
    employeeList,
    isEmployeeLoading,
    teamList,
    isLoadingTeam,
    isDeleteUserRolePending
  };
};

export default useTeamAssignment;
